/* eslint-disable max-lines */
import { batch } from 'react-redux';
// ---- external imports ---

import GameService from '~/services/game.service';
import {
	gameSelectors,
	turnSelectors,
	financialStatementsSelectors,
	uiActions,
	gameActions,
	loadingActions,
	playerHealthActions,
} from '~/store';
// ---- internal imports -----

import * as types from '../actionTypes';
// ---- parent imports ----

import {
	getErrorConfiguration,
	getErrorConfigurationAfterStatementUpdate,
} from './helpers';
import {
	isDevEnv,
	isTestEnv
} from '~/lib/helpers';
import {
	updateFinancialRatiosErrorField,
	updateFinancialRatiosFor,
	updateFinancialRatiosSubmissionStatusForQuarter,
	getErrorFieldForFinancialRatios
} from './financialRatios';

import {
	updateBalanceSheetFor,
	updateIncomeStatementFor,
} from './';
// ---- local imports -----


function financialStatementsLoadForNextTurn(financialStatements = null, didQuarterEnd = false) {
	return async (dispatch, getState) => {
		dispatch({ 
			type: types.FINANCIAL_STATEMENTS_NEXT_TURN_LOAD, 
			financialStatements: financialStatements,
			didQuarterEnd,
		});
	};
}

function financialStatementsLoad(financialStatements = null) {
	return async (dispatch, getState) => {
		try {

			if (!financialStatements) {
				const gameUserId = gameSelectors.getGameId(getState());
				const result = await GameService.getFinancialStatements(gameUserId);

				if (!result.isSuccess) {
					return;
				}

				financialStatements = result.data?.financialStatements;
			}

			dispatch({ type: types.FINANCIAL_STATEMENTS_LOAD,
				financialStatements: financialStatements, 
			 });

			 const {
				hasAttemptedToSubmitFinancialStatements,
				hasAttemptedToSubmitFinancialRatios
			} = financialStatements;
	
			//	we are looking at last quarter fs
			const quarterNum = turnSelectors.getCurrentQuarterNum(getState()) - 1;
			const { statementsByQuarterNum } = financialStatements;

			if (hasAttemptedToSubmitFinancialStatements) {
				const incomeStatement = statementsByQuarterNum[quarterNum].incomeStatement;
				let hasErrors = false;
				let errorConfig = 
					getErrorConfiguration(getState(), quarterNum, 'incomeStatement', incomeStatement);
	
				hasErrors = errorConfig.statement.length > 0;

				if (!hasErrors) {
					const balanceSheet = statementsByQuarterNum[quarterNum].balanceSheet;
					errorConfig = getErrorConfiguration(getState(), quarterNum, 'balanceSheet', balanceSheet);
					hasErrors = errorConfig.statement.length > 0;
				}

				if (hasErrors) {
					dispatch({ 
						type: types.FINANCIAL_STATEMENTS_SET_SUBMISSION_ERRORS_CONFIGURATION,
						errorConfiguration: errorConfig,
						displayedStatement: errorConfig.statement,
						hasAttemptedToSubmitFinancialStatements: true,
					});
					return;
				}
			}
	
			if (hasAttemptedToSubmitFinancialRatios) {
				const financialRatios = statementsByQuarterNum[quarterNum].financialRatios;
				const errorField = getErrorFieldForFinancialRatios(getState(), quarterNum, financialRatios);
	
				dispatch(updateFinancialRatiosErrorField(errorField));
			}
		} catch (error) {
			console.error(error);
		}
	};
}

function fillUserFinancialStatementsWithSystemStatements(
	quarterNum,
	systemFinancialStatements,
	shallAutomateFinancialRatios
) {
	return async(dispatch, getState) => {
		if (quarterNum === 0) {
			return;
		}

		const hasAlreadySubmittedFinancialStatements = financialStatementsSelectors
			.isFinancialStatementsSubmittedForQuarter(getState(), quarterNum);

		if (!hasAlreadySubmittedFinancialStatements)  {
			dispatch(updateIncomeStatementFor(
				quarterNum,
				systemFinancialStatements.incomeStatement
			));
			dispatch(updateBalanceSheetFor(
				quarterNum,
				systemFinancialStatements.balanceSheet
			));
		}
		const hasAlreadySubmittedFinancialRatios = financialStatementsSelectors
			.isFinancialRatiosSubmittedForQuarter(getState(), quarterNum);

		if (shallAutomateFinancialRatios && !hasAlreadySubmittedFinancialRatios) {
			dispatch(
				updateFinancialRatiosFor(quarterNum, systemFinancialStatements.financialRatios)
			);
		}
	};
}

function submitFinancialStatement(quarterNum) {
	return async (dispatch, getState) => {
		dispatch(uiActions.hideFinancialStatementTutorial());

		const errorConfiguration = getErrorConfiguration(getState(), quarterNum);
		const hasErrors = errorConfiguration.statement.length > 0;

		if (hasErrors) {
			dispatch({
				type: types.FINANCIAL_STATEMENTS_SET_HAS_ATTEMPTED_TO_SUBMIT_STATEMENTS,
				hasAttemptedToSubmitFinancialStatements: true,
			})
			dispatch({ 
				type: types.FINANCIAL_STATEMENTS_SET_SUBMISSION_ERRORS_CONFIGURATION,
				errorConfiguration,
				displayedStatement: errorConfiguration.statement,
				hasAttemptedToSubmitFinancialStatements: true,
			});
			return;
		}

		dispatch(loadingActions.start(types.FINANCIAL_STATEMENTS_UPDATE_SUBMISSION_STATUS_FOR_QUARTER));

		const financialStatements = financialStatementsSelectors
			.getFinancialStatementsFor(getState(), quarterNum);

		try {
			const gameUserId = gameSelectors.getGameId(getState());

			const result = await GameService.submitFinancialStatements(gameUserId, quarterNum);

			if (!result.isSuccess) {
				dispatch(loadingActions.endAfterNetworkFaliures());
				return;
			}

			dispatch(
				updateSubmissionStatusForQuarter(
					quarterNum,
					true,
					financialStatements
				)
			);
		}
		catch(err) {
			console.warn("error", err)
		};

		dispatch(loadingActions.end(types.FINANCIAL_STATEMENTS_UPDATE_SUBMISSION_STATUS_FOR_QUARTER));
	};
}

function updateErrorConfigurationAfterUserUpdate(quarterNum, statementType, statement) {
	return async (dispatch, getState) => {
		const errorConfiguration =
			getErrorConfigurationAfterStatementUpdate(getState(), quarterNum, statementType, statement);
		const displayedStatement = financialStatementsSelectors.getDisplayedStatement(getState());

		dispatch({ 
			type: types.FINANCIAL_STATEMENTS_SET_SUBMISSION_ERRORS_CONFIGURATION,
			errorConfiguration,
			displayedStatement,
		});
	};
}

function updateSubmissionStatusForQuarter(quarterNum, isSubmitted, financialStatements) {
	return { 
		type: types.FINANCIAL_STATEMENTS_UPDATE_SUBMISSION_STATUS_FOR_QUARTER,
		quarterNum,
		isSubmitted,
		financialStatements,	//	Added for missions
	};
}

function updateCanSubmitFinancialStatements(canSubmitFinancialStatements) {
	return { 
		type: types.FINANCIAL_STATEMENTS_UPDATE_CAN_SUBMIT_FINANCIAL_STATEMENTS,
		canSubmitFinancialStatements,
	};
}

function setDisplayedStatement(displayedStatement) {
	return { type: types.FINANCIAL_STATEMENTS_SET_DISPLAYED_STATEMENT, displayedStatement };
}

function setFinancialRatiosCardOpen(isOpen) {
	return {
		type: types.FINANCIAL_STATEMENTS_SET_FINANCIAL_RATIOS_CARD_OPEN,
		isOpen
	};
}

function openFinancialRatiosCard() {
	return setFinancialRatiosCardOpen(true);
}

function closeFinancialRatiosCard() {
	return setFinancialRatiosCardOpen(false);
}

function automateFinancialStatementsViaBookkeepers(
	quarterNum,
	systemFinancialStatements,
	shallAutomateFinancialRatios = false
) {
	return async (dispatch,) => {
		dispatch(fillUserFinancialStatementsWithSystemStatements(
			quarterNum,
			systemFinancialStatements,
			shallAutomateFinancialRatios
		));
		dispatch(updateSubmissionStatusForQuarter(
			quarterNum,
			true,
			systemFinancialStatements
		));
		if (shallAutomateFinancialRatios) {
			dispatch(updateFinancialRatiosSubmissionStatusForQuarter(
				quarterNum,
				true,
				systemFinancialStatements.financialRatios,
			));
		}
	};
}

function distributeProfit(amount) {
	return async (dispatch, getState) => {
		const turnDate = turnSelectors.getTurnDate(getState());
		const currTurnDate = new Date(turnDate);
		const month = currTurnDate.getMonth() + 1;
		const year = currTurnDate.getFullYear();
		const gameUserId = gameSelectors.getGameId(getState());
		const newGameCash = gameSelectors.getCash(getState()) - amount;

		dispatch(loadingActions.start(types.FINANCIAL_STATEMENTS_DISTRIBUTE_PROFIT));
		const result = await GameService.saveDistributedProfit(gameUserId, amount);

		if (!result.isSuccess) {
			dispatch(loadingActions.endAfterNetworkFaliures());

			return;
		}

		const { updatedPlayerHealth } = result.data;
		dispatch(loadingActions.end(types.FINANCIAL_STATEMENTS_DISTRIBUTE_PROFIT));

		batch(() => {
			dispatch(playerHealthActions.update(updatedPlayerHealth, false));
			dispatch(gameActions.updateCash(newGameCash));
			dispatch({
				type: types.FINANCIAL_STATEMENTS_DISTRIBUTE_PROFIT,
				amount,
				value: amount, // Added for positive accumelation mission handler
				month,
				year,
				quarterNum: turnSelectors.getCurrentQuarterNum(getState())
			});
		});
	};
}

function dangerousFillFinancialStatementsFromDevTools() {
	return (dispatch, getState) => {
		if (!isDevEnv() && !isTestEnv()) {
			return;
		}

		const state = getState();
		const currentQuarterNum = turnSelectors.getCurrentQuarterNum(state);
		const quarterToFill = currentQuarterNum - 1;

		if (quarterToFill <= 0) {
			console.warn('Cannot fill financial statements for quarter 0 or less');
			return;
		}

		const systemFinancialStatements = financialStatementsSelectors.getSystemFinancialStatementsFor(state, quarterToFill);

		if (!systemFinancialStatements) {
			console.warn(`No system financial statements found for quarter ${quarterToFill}`);
			return;
		}

		dispatch(fillUserFinancialStatementsWithSystemStatements(
			quarterToFill,
			systemFinancialStatements,
			true
		));

		dispatch(updateFinancialRatiosSubmissionStatusForQuarter(
			quarterToFill,
			true,
			systemFinancialStatements.financialRatios
		));
	};
}



export {
	financialStatementsLoad,
	financialStatementsLoadForNextTurn,
	submitFinancialStatement,
	updateSubmissionStatusForQuarter,
	updateCanSubmitFinancialStatements,
	automateFinancialStatementsViaBookkeepers,
	updateErrorConfigurationAfterUserUpdate,
	setDisplayedStatement,
	setFinancialRatiosCardOpen,
	openFinancialRatiosCard,
	closeFinancialRatiosCard,
	distributeProfit,
	dangerousFillFinancialStatementsFromDevTools,
	fillUserFinancialStatementsWithSystemStatements,
};
